/**
 * Browser Supabase client. Uses the anon key (safe to expose).
 * RLS policies in 0001_initial_schema.sql are what actually protect data.
 *
 * Per ADR-002: every authenticated request carries tenant_id in the JWT
 * custom claim. Sign-up and sign-in flows are responsible for resolving
 * tenant_id and setting the claim — see src/pages/SignIn.jsx (forthcoming).
 *
 * Impersonation overlay (Phase 1 Day 8, 2026-05-10): when an impersonation
 * token is present in sessionStorage, `getActiveBearerToken()` returns it
 * in place of the Supabase access token. Existing call sites that destructure
 * `(await supabase.auth.getSession()).data.session.access_token` keep
 * working because we wrap `getSession()` to substitute the access_token
 * with the impersonation token when one is active. Server side, `requireAuth`
 * detects the `pp_imp_` prefix and routes to the impersonation context.
 *
 * Why a wrapper, not a per-call-site refactor: there are 30+ /api/* call
 * sites and they all share the access_token destructure pattern. One
 * central swap is less surface area than 30 audit-prone edits, and the
 * wrapper preserves the exact promise shape callers already expect.
 */

import { createClient } from "@supabase/supabase-js";
import {
  getImpersonationToken,
  peekImpersonationClaims,
} from "./impersonation-client.js";

const _baseClient = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Wrap auth.getSession to swap access_token when an impersonation token is
// active. Other supabase.auth.* methods (signIn, signOut, onAuthStateChange)
// pass through unchanged so real-session lifecycle still flows correctly.
//
// Phase 1.6 Bug 3 fix (2026-05-12): when an imp token is present but there's
// no pre-existing customer-side Supabase session, SYNTHESIZE a minimal
// session from the imp token's claims so downstream consumers (useSession,
// /api/* fetch sites destructuring access_token) work in fresh-browser
// impersonation visits. Previous behavior: the wrap only fired when a real
// session existed, so opening the imp URL in a clean browser dropped to
// `result.data.session = null` and impersonation never engaged.
const _origGetSession = _baseClient.auth.getSession.bind(_baseClient.auth);

_baseClient.auth.getSession = async (...args) => {
  const result = await _origGetSession(...args);
  const impToken = getImpersonationToken();
  if (!impToken) return result;

  // Real session exists → overlay onto it (preserves real fields like
  // refresh_token, provider_token, expires_in for any consumer that
  // happens to read them).
  if (result?.data?.session) {
    return {
      ...result,
      data: {
        ...result.data,
        session: {
          ...result.data.session,
          access_token: impToken,
          _impersonated: true,
        },
      },
    };
  }

  // No real session → synthesize a minimal one from imp token claims so
  // fresh-browser impersonation works. peekImpersonationClaims reads the
  // JWT payload without signature verification (display-only; the server
  // re-verifies on every /api/* call). Bug 3 fix.
  const claims = peekImpersonationClaims(impToken);
  if (!claims) {
    // Token doesn't parse — bail to the original result rather than
    // returning a half-broken synthetic. The customer surface will fall
    // back to its signed-out treatment, which is honest.
    return result;
  }
  return {
    ...result,
    data: {
      ...(result?.data || {}),
      session: {
        access_token: impToken,
        expires_at: claims.session_expires_at || claims.exp,
        token_type: "bearer",
        user: {
          id: claims.target_user_id,
          email: null,
          aud: "authenticated",
        },
        _impersonated: true,
        _synthesized: true,
      },
    },
  };
};

export const supabase = _baseClient;

/**
 * Read-only convenience: returns the bearer token that /api/* calls should
 * carry. Prefer this for new code; existing code using
 * `(await supabase.auth.getSession()).data.session.access_token` keeps
 * working via the wrapper above.
 */
export async function getActiveBearerToken() {
  const impToken = getImpersonationToken();
  if (impToken) return impToken;
  const { data } = await _origGetSession();
  return data?.session?.access_token || null;
}
